import { ipfsToHttp } from "@/lib/image-upload";
import { createFileRoute, ClientOnly, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useAccount } from "wagmi";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { formatEther } from "viem";
import { Layers, Wallet } from "lucide-react";
import { networks } from "@/lib/wallet";

export const Route = createFileRoute("/collections")({
  component: () => (<ClientOnly fallback={null}><Collections /></ClientOnly>),
  head: () => ({ meta: [{ title: "Collections - RobotDex" }, { name: "description", content: "Browse NFT collections on RobotDex - your drops and the whole marketplace." }] }),
});

const CHAIN_NAME: Record<number, string> = Object.fromEntries(
  networks.map((n: any) => [Number(n.id), n.name ?? `Chain ${n.id}`]),
);

function Collections() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const { address, isConnected } = useAccount();

  const mine = useQuery({
    queryKey: ["my-drops", address],
    enabled: !!address,
    queryFn: async () => {
      const { data } = await supabase
        .from("nft_drops")
        .select("*")
        .eq("creator_wallet", address!.toLowerCase())
        .order("created_at", { ascending: false });
      return data ?? [];
    },
  });

  const all = useQuery({
    queryKey: ["all-drops"],
    queryFn: async () => {
      const { data } = await supabase
        .from("nft_drops")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(60);
      return data ?? [];
    },
  });

  if (!mounted) return <div className="p-8 text-muted-foreground">Loading…</div>;

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
      <PageHeader title="Collections" subtitle="Browse NFT collections across RobotDex" />

      <Tabs defaultValue={isConnected ? "mine" : "market"}>
        <TabsList className="neon-border">
          <TabsTrigger value="mine">
            <Wallet className="h-3.5 w-3.5 mr-1" /> My Collections
          </TabsTrigger>
          <TabsTrigger value="market">
            <Layers className="h-3.5 w-3.5 mr-1" /> Marketplace
          </TabsTrigger>
        </TabsList>

        <TabsContent value="mine" className="mt-4">
          {!isConnected ? (
            <Card className="p-10 text-center neon-border bg-card/60">
              <p className="text-muted-foreground">Connect your wallet to see your collections.</p>
            </Card>
          ) : mine.isLoading ? (
            <SkeletonGrid />
          ) : !mine.data || mine.data.length === 0 ? (
            <Card className="p-10 text-center neon-border bg-card/60">
              <p className="text-muted-foreground">You haven't created any drops yet.</p>
              <div className="mt-4"><Button asChild className="neon-glow"><Link to="/create">Create your first drop</Link></Button></div>
            </Card>
          ) : (
            <Grid drops={mine.data} />
          )}
        </TabsContent>

        <TabsContent value="market" className="mt-4">
          {all.isLoading ? (
            <SkeletonGrid />
          ) : !all.data || all.data.length === 0 ? (
            <Card className="p-10 text-center neon-border bg-card/60">
              <p className="text-muted-foreground">No collections yet. Be the first!</p>
            </Card>
          ) : (
            <Grid drops={all.data} />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Grid({ drops }: { drops: any[] }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {drops.map((d) => <DropCard key={d.id} d={d} />)}
    </div>
  );
}

function DropCard({ d }: { d: any }) {
  const price = d.mint_price_wei && d.mint_price_wei !== "0"
    ? `${Number(formatEther(BigInt(d.mint_price_wei))).toFixed(4)}`
    : "Free";
  const supply = d.max_supply ? `${d.minted_count} / ${d.max_supply}` : `${d.minted_count} / ∞`;
  return (
    <Link to="/drop/$id" params={{ id: d.id }} className="block">
      <Card className="p-4 bg-card/60 neon-border hover:neon-glow transition-all">
        <div className="flex items-center gap-3">
          {d.logo_url ? (
            <img src={ipfsToHttp(d.logo_url)} alt="" loading="lazy" className="h-14 w-14 rounded-md object-cover" />
          ) : (
            <div className="h-14 w-14 rounded-md bg-primary/20 flex items-center justify-center font-black neon-text">
              {d.symbol?.slice(0, 2)}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="font-semibold truncate">{d.name}</div>
            <div className="text-xs text-muted-foreground truncate">{d.symbol} · {CHAIN_NAME[d.chain_id] ?? `chain ${d.chain_id}`}</div>
          </div>
          <Badge variant="outline" className="neon-border uppercase text-[10px]">{d.status}</Badge>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2 text-center text-[11px] uppercase tracking-wider text-muted-foreground">
          <Stat label="Price" value={price} />
          <Stat label="Minted" value={supply} />
          <Stat label="Created" value={new Date(d.created_at).toLocaleDateString()} />
        </div>
      </Card>
    </Link>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded border border-border/60 bg-background/40 py-1.5">
      <div className="text-[9px]">{label}</div>
      <div className="text-xs text-foreground font-semibold normal-case truncate">{value}</div>
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <Card key={i} className="p-4 bg-card/60 neon-border animate-pulse">
          <div className="flex items-center gap-3">
            <div className="h-14 w-14 rounded-md bg-muted/40" />
            <div className="flex-1 space-y-2">
              <div className="h-3 w-32 bg-muted/40 rounded" />
              <div className="h-2 w-20 bg-muted/30 rounded" />
            </div>
          </div>
          <div className="mt-3 h-8 bg-muted/20 rounded" />
        </Card>
      ))}
    </div>
  );
}
